import React from 'react';
import { Formik, FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { toast } from 'react-toastify';
import { Box, Button, Card, IconButton } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { isEmpty } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { sanitizeFormValues, translate } from '../../../../utils';
import { FormSubmitType } from '../../../../types';
import { Toolbar, Typography } from '../../../../ui-kit';
import { newDeliveryListValidationSchema, verifyDeliveryListValidationSchema } from './validation';
import { Stepper } from './Stepper';
import { BasicInformation, CheckDeliveryList, ConnectCards, PickIngredients } from './FormSteps';
import { DeliveryListFormValues, SlideDirection, Step } from './types';
import { StepContextProvider, useStepContext } from './useStepsContext';

interface DeliveryListItemError {
    quantity: string;
    priceWithoutVat: string;
    totalCost: string;
}

interface PendingListItemError {
    quantity: string;
    unitPriceWithoutVat: string;
    stockCard: string;
    unitOfMeasure: string;
    unitPrice: string;
    finalAmount: string;
}

const createSteps: Step[] = [
    { title: translate('Basic information'), component: <BasicInformation key='BasicInformation' /> },
    { title: translate('Pick ingredients'), component: <PickIngredients key='PickIngredients' /> },
    { title: translate('Check your delivery list'), component: <CheckDeliveryList key='CheckDeliveryList' /> },
];

const verifySteps: Step[] = [
    { title: translate('Basic information'), component: <BasicInformation key='BasicInformation' /> },
    { title: translate('Check invoice and connect to existing cards'), component: <ConnectCards key='ConnectCards' /> },
    { title: translate('Check your delivery list'), component: <CheckDeliveryList key='CheckDeliveryList' /> },
];

type OnSubmit = FormSubmitType<DeliveryListFormValues>;

interface DeliveryListFormComponentProps {
    onClose: () => void;
    dialogName: string;
    steps: Step[];
}

const DeliveryListFormComponent = ({
    onClose,
    dialogName,
    steps,
}: DeliveryListFormComponentProps) => {
    const { currentStep, setCurrentStep } = useStepContext();
    const [slideDirection, setSlideDirection] = React.useState<SlideDirection | undefined>();
    const { validateForm, values, setTouched, submitForm, isSubmitting } = useFormikContext();
    const { t } = useTranslation();

    const handleNext = async () => {
        const errors: FormikErrors<DeliveryListFormValues> = await validateForm(values);
        const pendingListItemErrors = errors.pendingListItems as unknown as PendingListItemError[] | string | undefined;
        if (pendingListItemErrors) {
            if (typeof pendingListItemErrors !== 'string') {
                const keys: Array<keyof PendingListItemError> = [
                    'stockCard',
                    'quantity',
                    'unitPriceWithoutVat',
                    'unitPrice',
                    'unitOfMeasure',
                    'finalAmount',
                ];

                keys.forEach(key => {
                    const error = pendingListItemErrors.find(e => e && e[key]);
                    if (error) {
                        toast(error[key], { type: 'warning' });
                    }
                });
            }
        }

        const deliveryListItemsErrors = errors.deliveryListItems as unknown as DeliveryListItemError[] | string | undefined;
        if (deliveryListItemsErrors) {
            if (typeof deliveryListItemsErrors !== 'string') {
                const errorsArray: FormikTouched<DeliveryListFormValues['deliveryListItems']> = deliveryListItemsErrors.map(cur => {
                    if (!cur) {
                        return undefined;
                    }
                    const { quantity, totalCost, priceWithoutVat } = cur;
                    return {
                        quantity: !!quantity,
                        totalCost: !!totalCost,
                        priceWithoutVat: !!priceWithoutVat,
                    };
                });
                setTouched({ deliveryListItems: errorsArray });
            } else {
                toast(t('Delivery items are required'), { type: 'warning' });
            }

        } else {
            const errorFieldKeys = Object
                .keys(errors)
                .reduce((acc: FormikTouched<DeliveryListFormValues>, cur) => ({ ...acc, [cur]: true }), {});

            setTouched(errorFieldKeys);
        }


        if (isEmpty(errors)) {
            if (!slideDirection || slideDirection === 'right') {
                setSlideDirection('left');
            }
            setCurrentStep(currentStep as number + 1);
        }
    };

    const handleBack = () => {
        if (!slideDirection || slideDirection === 'left') {
            setSlideDirection('right');
        }
        setCurrentStep(currentStep - 1);
    };

    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === steps.length - 1;

    return (
        <Card style={{ height: '100%', overflowY: 'auto' }}>
            <Box px={3} py={2}>
                <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                    <ArrowBack />
                </IconButton>
                <Box mb={2} />
                <Typography variant='h6' component='h1'>{dialogName}</Typography>
                <Stepper
                    activeStep={currentStep}
                    steps={steps}
                    slideDirection={slideDirection}
                />
            </Box>
            <Toolbar isBottom disableDrawerOffset>
                <Box display='flex' justifyContent='center' flexGrow={1}>
                    {!isFirstStep
                    ? (
                        <>
                            <Button
                                onClick={handleBack}
                            >
                                {t('Back')}
                            </Button>
                            <Box mr={.5} />
                        </>

                    ) : null}
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        onClick={isLastStep ? submitForm : handleNext}
                    >
                        {isLastStep ? t('Submit') : t('Next')}
                    </Button>
                </Box>
            </Toolbar>
        </Card>
    );
};

interface DeliveryListFormProps {
    onSubmit: OnSubmit;
    onClose: () => void;
    initialValues?: DeliveryListFormValues;
    dialogName: string;
    verifyingUploadId?: string | null;
}

const DeliveryListForm = ({ initialValues, onSubmit, dialogName, onClose, verifyingUploadId }: DeliveryListFormProps) => {
    const { currentStep } = useStepContext();
    const defaultValues: DeliveryListFormValues = {
        invoiceNumber: '',
        supplierId: '',
        deliveryDateUtc: new Date(),
        deliveryListItems: [],
        pendingListItems: [],
    };

    const handleSubmit = async (values: DeliveryListFormValues) => {
        const finalValues = sanitizeFormValues(values);
        return onSubmit(finalValues);
    };

    const steps = verifyingUploadId ? verifySteps : createSteps;
    const validationSchema = verifyingUploadId ? verifyDeliveryListValidationSchema : newDeliveryListValidationSchema;

    const currentValidationSchema = validationSchema[currentStep];

    return (
        <Formik
            initialValues={{ ...defaultValues, ...initialValues }}
            validationSchema={currentValidationSchema}
            onSubmit={handleSubmit}
        >
            <DeliveryListFormComponent
                dialogName={dialogName}
                onClose={onClose}
                steps={steps}
            />
        </Formik>
    );
};

export default (props: DeliveryListFormProps) => (
    <StepContextProvider>
        <DeliveryListForm {...props} />
    </StepContextProvider>
);
